import { useMemo } from "react";
import { useParams } from "react-router-dom";

import type {
  PinnedVersionSelectorLabel,
  PinnedVersionSelectorVersion,
} from "@/components/SharedSessions";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePromptVersions } from "@/queries";
import { usePromptLabels } from "@/queries/prompt-labels";

export const usePromptSharePinnedVersionData = (): {
  versions: PinnedVersionSelectorVersion[];
  labels: PinnedVersionSelectorLabel[];
} => {
  const { promptId } = useParams();
  const { activeWorkspaceId } = useUser();
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const numericPromptId = promptId ? Number(promptId) : undefined;

  const promptVersionsQuery = usePromptVersions(userToken, {
    promptRegistryId: numericPromptId,
    workspaceId: activeWorkspaceId!,
  });

  const { data: labelsData } = usePromptLabels({
    userToken,
    workspace_id: activeWorkspaceId!,
    prompt_id: numericPromptId,
    enabled: !!activeWorkspaceId && !!numericPromptId,
  });

  const rawVersions = useMemo(() => {
    if (!promptVersionsQuery.data?.pages) {
      return [];
    }
    return promptVersionsQuery.data.pages.flatMap(page => page.prompt_versions ?? []);
  }, [promptVersionsQuery.data]);

  const rawLabels = useMemo(() => labelsData?.prompt_labels ?? [], [labelsData]);

  return useMemo(() => {
    const versions = rawVersions.map(v => ({ number: v.number }));
    const idToNumber = new Map(rawVersions.map(v => [v.id, v.number]));
    const labels: PinnedVersionSelectorLabel[] = [];
    for (const label of rawLabels) {
      const versionNumber = idToNumber.get(label.prompt_version_id);
      if (versionNumber !== undefined) {
        labels.push({ id: label.id, name: label.name, versionNumber });
      }
    }
    return { versions, labels };
  }, [rawLabels, rawVersions]);
};
